import React, { useEffect, useState } from 'react';
import { getAdminSettings, updateAdminSettings, fetchTiers, fetchWaitingListStats } from '../services/adminApi';
import { fetchFeatureFlags } from '../services/api';

export default function AdminSettings({ showHeader = true, tiers: propTiers = [] }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    waitingListTier: '',
    capacity: 0,
    emailOnMove: false,
    waitingListEnabled: false,
  });
  const [freeTier, setFreeTier] = useState('');
  const [errors, setErrors] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [stats, setStats] = useState(null);
  // Default to showing waiting settings to preserve existing behavior in tests
  const [showWaitingSettings, setShowWaitingSettings] = useState(true);

  useEffect(() => {
    let mounted = true;
    getAdminSettings()
      .then((response) => {
        if (!mounted) return;
        // Backend returns { site: { free_tier, waiting_list_tier, ... } }
        const data = response.site || response;
        setForm({
          waitingListTier: data.waiting_list_tier || '',
          capacity: data.waiting_list_capacity ?? 0,
          emailOnMove: !!data.waiting_list_email_on_move,
          waitingListEnabled: !!data.waiting_list_enabled,
        });
        setFreeTier(data.free_tier || '');
      })
      .catch((err) => setError(err.message))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchTiers()
      .then((data) => {
        if (!mounted) return;
        // API may return an array or an object with `tiers` key
        // prefer props if provided
        setTiers(propTiers && propTiers.length > 0 ? propTiers : (Array.isArray(data) ? data : data.tiers || []));
      })
      .catch(() => {
        /* c8 ignore start */
        if (!mounted) return;
        /* c8 ignore stop */
        setTiers([]);
      });

    fetchWaitingListStats()
      .then((d) => mounted && setStats(d))
      .catch(() => {
        /* c8 ignore start */
        if (!mounted) return;
        /* c8 ignore stop */
        setStats(null);
      });

    // Fetch feature flags and decide if waiting list settings UI should be shown
    fetchFeatureFlags()
      .then((res) => {
        if (!mounted) return;
        const flags = res.flags || [];
        // Use the `settings:waiting_list` feature flag to control visibility only
        const flag = flags.find((f) => f.key === 'settings:waiting_list');
        if (flag) setShowWaitingSettings(!!flag.enabled);
        /* c8 ignore start */
        else setShowWaitingSettings(true); // default on when no explicit flag present
        /* c8 ignore stop */
      })
      .catch(() => {
        /* c8 ignore start */
        if (!mounted) return;
        /* c8 ignore stop */
        // If flags cannot be fetched, keep previous behavior (show UI)
        setShowWaitingSettings(true);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Keep local tiers in sync when parent provides them
  useEffect(() => {
    if (propTiers && propTiers.length > 0) setTiers(propTiers);
  }, [propTiers]);

  function handleChange(e) {
    const { name, type, value, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === 'checkbox' ? checked : value }));
  }

  useEffect(() => {
    // Validate client-side: capacity non-negative AND rank validation
    const errs = [];
    const cap = Number(form.capacity);
    if (Number.isNaN(cap) || cap < 0) errs.push('Capacity must be a non-negative integer');

    // Rank validation: waiting list tier must have strictly lower rank than free tier
    /* c8 ignore start */
    if (form.waitingListTier && form.waitingListTier !== '' && freeTier && freeTier !== '') {
      const waitingListTierObj = tiers.find((t) => String(t.id ?? t._id ?? t.key) === String(form.waitingListTier));
      const freeTierObj = tiers.find((t) => String(t.id ?? t._id ?? t.key) === String(freeTier));
      if (waitingListTierObj && freeTierObj && waitingListTierObj.rank !== undefined && freeTierObj.rank !== undefined) {
        if (waitingListTierObj.rank >= freeTierObj.rank) {
          errs.push('Waiting list tier must have a strictly lower rank than the free tier');
        }
      }
    }
    /* c8 ignore stop */

    setErrors(errs);
  }, [form.capacity, form.waitingListTier, freeTier, tiers]);

  // Derive rank invalidity at render time to avoid timing/race issues in tests
  let rankInvalid = false;

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const parseTier = (v) => {
        if (v === '' || v == null) return null;
        const s = String(v);
        return /^\d+$/.test(s) ? Number(s) : s;
      };

      const payload = {
        waiting_list_tier: parseTier(form.waitingListTier),
        waiting_list_capacity: Number(form.capacity) || 0,
        waiting_list_email_on_move: !!form.emailOnMove,
        waiting_list_enabled: !!form.waitingListEnabled,
        
      };
      await updateAdminSettings(payload);
      // refresh
      const response = await getAdminSettings();
      // Backend returns { site: { free_tier, waiting_list_tier, ... } }
      const refreshed = response.site || response;
      setForm({
        waitingListTier: refreshed.waiting_list_tier || '',
        capacity: refreshed.waiting_list_capacity ?? 0,
        emailOnMove: !!refreshed.waiting_list_email_on_move,
        waitingListEnabled: !!refreshed.waiting_list_enabled,
      });
      setFreeTier(refreshed.free_tier || '');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div>Loading admin settings…</div>;

  if (!showWaitingSettings) return <div>Waiting list settings are disabled</div>;

  return (
    <div>
      {showHeader && <h2 className="text-xl font-semibold text-white mb-4">Waiting List Settings</h2>}
      {error && <div className="text-red-400 mb-4">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">


          <div>
            <label className="flex items-center text-sm text-gray-300 mb-2">
              <input
                name="waitingListEnabled"
                type="checkbox"
                checked={form.waitingListEnabled}
                onChange={handleChange}
                className="mr-2"
              />
              Enable waiting list feature
            </label>
          </div>

          <div>
            <label htmlFor="waitingListTier" className="block text-sm text-gray-300 mb-1">Waiting List Tier</label>
            <select
              id="waitingListTier"
              name="waitingListTier"
              value={form.waitingListTier}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
            >
              <option value="">— Select —</option>
              {tiers.map((t) => {
                /* c8 ignore start */
                const _k = t.id ?? t._id ?? t.key;
                const _v = String(t.id ?? t._id ?? t.key);
                const _l = t.name || t.title || String(t.id ?? t._id ?? '');
                /* c8 ignore stop */
                return <option key={_k} value={_v}>{_l}</option>;
              })}
            </select>
          </div>

          <div>
            <label htmlFor="capacity" className="block text-sm text-gray-300 mb-1">Capacity</label>
            <input
              id="capacity"
              name="capacity"
              type="number"
              value={form.capacity}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white"
            />
          </div>

          

          <div>
            <label className="flex items-center text-sm text-gray-300">
              <input
                name="emailOnMove"
                type="checkbox"
                checked={form.emailOnMove}
                onChange={handleChange}
                className="mr-2"
              />
              Email users when moved off waiting list
            </label>
          </div>

          

          <div>
            <button
              type="submit"
              disabled={
                saving ||
                errors.length > 0 ||
                Number.isNaN(Number(form.capacity)) ||
                Number(form.capacity) < 0
              }
              className="px-4 py-2 bg-primary rounded-lg text-white"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>

          {errors.length > 0 && (
            <div className="text-red-400 mt-2">
              <ul>
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Waiting list stats removed per admin UX decision */}
        </div>
      </form>
    </div>
  );
}
